import React, { useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectServiceCenterId } from "../../../../../login/loginSelectors"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import {
    fetchOccupancyRatePerResource,
    ServiceCenterOccupancyRateReportResponse,
    ServiceCenterStatisticsQuery,
} from "../../statisticsApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { StandardExportButton } from "../../../../../../shared/components/table/StandardExportButton"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import {
    DateFilterParams,
    useInitialDateFilters,
} from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardChip } from "../../../../../../shared/components/badges/StandardChip"
import { MRT_ColumnDef, MRT_TableInstance } from "material-react-table"
import { OccupancyRateEntry } from "../../statisticsTypes"
import { ResourceTypeFilter } from "../common/ResourceTypeFilter"
import { ResourceType } from "../../../../admin/organization/resources/resourcesTypes"
import { useExportRateEntry } from "../../hooks/useExportRateEntry"
import { useDateTime } from "../../../../../localization/useDateTime"

const selectOccupancies = (data: ServiceCenterOccupancyRateReportResponse) => data?.occupancyRates || []

export function Occupancy() {
    const serviceCenterId = useAppSelector(selectServiceCenterId)
    const [selectedResource, setSelectedResource] = useState<ResourceType>(ResourceType.ROOM)
    const { l10n } = useAppLocalization()
    const { startDate, endDate } = useInitialDateFilters()
    const dateTimeFactory = useDateTime()
    const today = dateTimeFactory.now()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const tableInstanceRef = useRef<MRT_TableInstance<OccupancyRateEntry>>(null)

    const occupanciesResult = useApiQuery(fetchOccupancyRatePerResource, {
        queryName: ServiceCenterStatisticsQuery.SERVICE_CENTER_STATISTICS_OCCUPANCY_RATE_PER_RESOURCE,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            resourceType: selectedResource,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectOccupancies,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(occupanciesResult)

    const columns = useMemo<MRT_ColumnDef<OccupancyRateEntry>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-resource-title"),
                accessorKey: "resource.displayName",
            }),

            columnFactory.nameColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-occupancy-rate-title"),
                accessorKey: "occupancyRate",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("service-center-reports-statistic-occupancy-table-status-title"),
                id: "visibility",
                Cell: ({ row }) => {
                    return (
                        <StandardChip
                            label={row.original.status.label}
                            variant={row.original.status.variant}
                            placement="table"
                        />
                    )
                },
            }),
        ],
        [l10n, columnFactory],
    )

    const exportOccupancies = useExportRateEntry({
        data: occupanciesResult.data,
        startDate: dateState.startDate || today,
        endDate: dateState.endDate || today,
        isOccupancyRate: true,
    })

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                initialState={{ density: "spacious" }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton
                            onClick={() => {
                                exportOccupancies()
                            }}
                        />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-statistic-occupancy-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} label="Date range" />
                        </LocalizedStrict>
                        <ResourceTypeFilter value={selectedResource} onChange={setSelectedResource} />
                    </StandardTableFilter>
                )}
            />
        </>
    )
}
